import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FileUp, X } from "lucide-react";
import { cn } from "@/lib/cn";
import { formatFileSize } from "@/api/clientMaterials";
import {
  KbDuplicateExisting,
  KbFolder,
  KbUploadError,
  newKbRequestId,
  uploadKbFile,
} from "@/api/knowledge";
import { KbDuplicateDialog } from "./KbDuplicateDialog";

/**
 * D-147 W1 S4 — «Додати файл» (visual package v1.2 LOCK, екран ⑤).
 * Відкривається ЛИШЕ з розділу (розділ = readonly з контексту); результат =
 * ЗАВЖДИ чернетка («опублікувати одразу» не існує) → редірект на картку
 * (вкладка «Чернетка»). Дублікати — ДВА чеки (binary-hash + normalized-title),
 * обидва через єдиний діалог; confirm-прапори накопичуються, retry під тим
 * самим requestId (ledger re-arm). Валідація типу/розміру — синхронна
 * server-side (422 → інлайн-помилка); транспорт = XHR із чесним прогресом.
 */
const ACCEPT = ".pdf,.docx,.xlsx,.pptx,.jpg,.jpeg,.png";
const MAX_BYTES = 20 * 1024 * 1024;

type Phase =
  | { kind: "form" }
  | { kind: "uploading"; percent: number }
  | { kind: "duplicate"; dupKind: "file" | "title"; existing: KbDuplicateExisting | null };

export function KbFileUploadModal({
  sectionId,
  sectionName,
  ownerName,
  ownerGrade,
  folders,
  onClose,
}: {
  sectionId: string;
  sectionName: string;
  /** Власник розділу — для banner-примітки editor-а. */
  ownerName: string;
  ownerGrade: boolean;
  folders: KbFolder[];
  onClose: () => void;
}) {
  const navigate = useNavigate();
  const [file, setFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [title, setTitle] = useState("");
  const [touched, setTouched] = useState(false);
  const [folderId, setFolderId] = useState("");
  const [phase, setPhase] = useState<Phase>({ kind: "form" });
  const [error, setError] = useState<string | null>(null);
  const [requestId] = useState(newKbRequestId);
  const confirmsRef = useRef({ confirmDuplicateFile: false, confirmDuplicateTitle: false });
  const abortRef = useRef<(() => void) | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const busy = phase.kind === "uploading";

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !busy) onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [busy, onClose]);

  const pickFile = (f: File | null) => {
    setFileError(null);
    setError(null);
    if (!f) {
      setFile(null);
      return;
    }
    if (f.size > MAX_BYTES) {
      setFile(null);
      setFileError("Файл більший за 20 МБ.");
      return;
    }
    setFile(f);
    if (title.trim() === "") {
      setTitle(f.name.replace(/\.[^.]+$/, ""));
    }
  };

  const submit = () => {
    setTouched(true);
    const trimmed = title.trim();
    if (!file || trimmed === "") return;
    setError(null);
    setPhase({ kind: "uploading", percent: 0 });

    const { promise, abort } = uploadKbFile(
      sectionId,
      file,
      {
        title: trimmed,
        folderId: folderId || null,
        requestId,
        ...confirmsRef.current,
      },
      (loaded, total) =>
        setPhase({ kind: "uploading", percent: total > 0 ? Math.round((loaded / total) * 100) : 0 }),
    );
    abortRef.current = abort;

    promise
      .then((r) => navigate(`/knowledge/items/${r.itemId}?view=draft`))
      .catch((e: unknown) => {
        if (e instanceof KbUploadError && e.status === 409) {
          if (e.code === "duplicate_file") {
            setPhase({ kind: "duplicate", dupKind: "file", existing: e.existing ?? null });
            return;
          }
          if (e.code === "duplicate_title") {
            setPhase({ kind: "duplicate", dupKind: "title", existing: e.existing ?? null });
            return;
          }
        }
        setPhase({ kind: "form" });
        setError(e instanceof Error ? e.message : "Не вдалося завантажити файл.");
      });
  };

  return (
    <>
      <div
        className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 p-0 sm:p-4"
        role="dialog"
        aria-modal="true"
        aria-label="Додати файл"
      >
        <div className="flex h-full w-full flex-col overflow-y-auto rounded-none border bg-card shadow-2xl sm:h-auto sm:max-w-lg sm:rounded-xl">
          <div className="flex items-center justify-between border-b px-5 py-3.5">
            <span className="text-sm font-bold">Додати файл</span>
            <button
              type="button"
              onClick={onClose}
              disabled={busy}
              aria-label="Закрити"
              className="rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-50"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          <div className="flex-1 px-5 py-4">
            <div className="mb-3">
              <label className="mb-1 block text-xs font-semibold">Розділ</label>
              <input
                readOnly
                value={`${sectionName} (з контексту розділу)`}
                className="w-full rounded-lg border bg-muted px-3 py-2 text-sm text-muted-foreground outline-none"
              />
            </div>
            <div className="mb-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div>
                <label className="mb-1 block text-xs font-semibold" htmlFor="kb-fu-folder">
                  Папка
                </label>
                <select
                  id="kb-fu-folder"
                  value={folderId}
                  disabled={busy}
                  onChange={(e) => setFolderId(e.target.value)}
                  className="w-full rounded-lg border bg-background px-3 py-2 text-sm outline-none focus:ring-1 focus:ring-ring"
                >
                  <option value="">— без папки —</option>
                  {folders.map((f) => (
                    <option key={f.id} value={f.id}>
                      {f.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="mb-1 block text-xs font-semibold">Відповідальний</label>
                <input
                  readOnly
                  value="Я (за замовчуванням)"
                  className="w-full rounded-lg border bg-muted px-3 py-2 text-sm text-muted-foreground outline-none"
                />
              </div>
            </div>
            <div className="mb-3">
              <label className="mb-1 block text-xs font-semibold" htmlFor="kb-fu-title">
                Назва <span className="text-destructive">*</span>
              </label>
              <input
                id="kb-fu-title"
                value={title}
                disabled={busy}
                placeholder="Людська назва матеріалу"
                onChange={(e) => setTitle(e.target.value)}
                className="w-full rounded-lg border bg-background px-3 py-2 text-sm outline-none focus:ring-1 focus:ring-ring"
              />
              {touched && title.trim() === "" && (
                <p role="alert" className="mt-1 text-xs text-destructive">
                  Порожня назва.
                </p>
              )}
            </div>
            <div className="mb-3">
              <label className="mb-1 block text-xs font-semibold">
                Файл <span className="text-destructive">*</span>
              </label>
              <button
                type="button"
                disabled={busy}
                onClick={() => inputRef.current?.click()}
                className={cn(
                  "w-full rounded-lg border-2 border-dashed px-4 py-6 text-center text-sm text-muted-foreground hover:border-primary focus-visible:border-primary focus-visible:outline-none",
                  fileError && "border-destructive",
                )}
              >
                <FileUp className="mx-auto mb-1.5 h-5 w-5" aria-hidden />
                Перетягніть файл сюди або <span className="font-semibold text-primary">оберіть</span>
                <span className="mt-1 block text-[11px]">PDF, DOCX, XLSX, PPTX, JPG, PNG · до 20 МБ</span>
              </button>
              <input
                ref={inputRef}
                type="file"
                accept={ACCEPT}
                className="hidden"
                onChange={(e) => {
                  pickFile(e.target.files?.[0] ?? null);
                  e.target.value = "";
                }}
              />
              {fileError && (
                <p role="alert" className="mt-1 text-xs text-destructive">
                  {fileError}
                </p>
              )}
              {file && (
                <p className="mt-1.5 text-xs text-muted-foreground">
                  📎 {file.name} · {formatFileSize(file.size)}
                  <span className="mt-0.5 block text-[11px]">
                    Ім'я оригіналу незмінне; пошук по назві та оригіналу.
                  </span>
                </p>
              )}
            </div>

            {phase.kind === "uploading" && (
              <div className="mb-3">
                <div className="h-2 overflow-hidden rounded-full bg-muted">
                  <div
                    className="h-full bg-primary transition-all"
                    style={{ width: `${phase.percent}%` }}
                  />
                </div>
                <p className="mt-1 text-xs text-muted-foreground">Завантажуємо… {phase.percent}%</p>
              </div>
            )}
            {error && (
              <p role="alert" className="mb-3 text-xs text-destructive">
                {error}
              </p>
            )}

            <div className="rounded-xl bg-info px-3.5 py-2.5 text-sm text-info-foreground">
              Файл буде збережено як <b>чернетку</b>. Публікація — з картки матеріалу
              {!ownerGrade && ownerName ? ` (публікує власник розділу — ${ownerName})` : ""}.
            </div>
          </div>

          <div className="flex justify-end gap-2 border-t px-5 py-3.5">
            <button
              type="button"
              onClick={busy ? () => abortRef.current?.() : onClose}
              className="rounded-md border px-3.5 py-2 text-sm font-medium hover:bg-accent"
            >
              Скасувати
            </button>
            <button
              type="button"
              disabled={busy || !file || title.trim() === ""}
              onClick={submit}
              className="rounded-md bg-primary px-3.5 py-2 text-sm font-semibold text-primary-foreground disabled:opacity-50"
            >
              Зберегти чернетку
            </button>
          </div>
        </div>
      </div>

      {phase.kind === "duplicate" && (
        <KbDuplicateDialog
          kind={phase.dupKind}
          existing={phase.existing?.itemId ? phase.existing : null}
          confirmLabel="Завантажити як окремий"
          onCancel={() => setPhase({ kind: "form" })}
          onConfirm={() => {
            if (phase.dupKind === "file") confirmsRef.current.confirmDuplicateFile = true;
            else confirmsRef.current.confirmDuplicateTitle = true;
            setPhase({ kind: "form" });
            submit();
          }}
        />
      )}
    </>
  );
}
